import { ethers } from "ethers";
import config from "../config/index.js";
import { resolveProvider } from "./wallets.js";

let activeProviderEip1193 = null;

/**
 * Returns a read-only JSON RPC provider configured from VITE_RPC_URL.
 * Use this for view calls when no wallet is connected.
 */
export function getReadProvider() {
    return new ethers.JsonRpcProvider(config.network.rpcUrl);
}

/**
 * Records the EIP-1193 provider object the user picked from the wallet
 * modal. Subsequent `getBrowserProvider()` calls will wrap that
 * provider rather than the default `window.ethereum`, which is what
 * lets MetaMask + Rabby + Coinbase coexist on the same browser.
 */
export function setActiveProvider(eip1193Provider) {
    activeProviderEip1193 = eip1193Provider || null;
}

/**
 * Returns a BrowserProvider wrapping whichever wallet the user
 * selected via the EIP-6963 modal. Falls back to `window.ethereum` so
 * legacy single-wallet setups continue to work without ceremony.
 * Throws when no injected wallet is available at all.
 */
export function getBrowserProvider() {
    const eip1193 = activeProviderEip1193 || resolveProvider(null);
    if (!eip1193) {
        throw new Error("No injected wallet detected. Please install MetaMask, Rabby or Coinbase Wallet.");
    }
    return new ethers.BrowserProvider(eip1193);
}

/** Returns the active EIP-1193 provider object (selected wallet or fallback). */
export function getActiveEip1193Provider() {
    return activeProviderEip1193 || resolveProvider(null);
}

/** Builds the wallet_addEthereumChain payload from the configured network. */
function buildAddChainParams() {
    const hexChainId = "0x" + Number(config.network.chainId).toString(16);
    return {
        chainId: hexChainId,
        chainName: config.network.name,
        nativeCurrency: config.network.nativeCurrency,
        rpcUrls: [config.network.rpcUrl],
        blockExplorerUrls: [config.network.blockExplorerUrl]
    };
}

/**
 * Asks the active wallet to switch to the configured chain. If the
 * chain is not known to the wallet (error code 4902) the chain is
 * added first and then the switch is retried.
 */
export async function ensureChain() {
    const provider = getActiveEip1193Provider();
    if (!provider) return;
    const params = buildAddChainParams();
    try {
        await provider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: params.chainId }]
        });
    } catch (err) {
        if (err && (err.code === 4902 || err.code === -32603)) {
            await provider.request({
                method: "wallet_addEthereumChain",
                params: [params]
            });
            await provider.request({
                method: "wallet_switchEthereumChain",
                params: [{ chainId: params.chainId }]
            });
            return;
        }
        throw err;
    }
}

/**
 * Requests account access from the injected wallet, ensures the active
 * chain matches the configured network, and returns a connected signer.
 *
 * For providers that already have accounts populated (typically
 * WalletConnect right after `provider.connect()` resolves) we skip the
 * `eth_requestAccounts` round-trip — on mobile wallets that call would
 * re-prompt the user, and waiting on a second approval can hang the
 * connect flow indefinitely. We also tolerate `ensureChain()` failures
 * for the same reason: the WC session is opened on the configured chain
 * during init, so an extra `wallet_switchEthereumChain` is redundant
 * and not always supported by every wallet.
 */
export async function connectWallet() {
    const provider = getBrowserProvider();
    const eip1193 = getActiveEip1193Provider();

    let accounts = Array.isArray(eip1193?.accounts) ? eip1193.accounts : null;
    if (!accounts || accounts.length === 0) {
        try {
            accounts = await provider.send("eth_requestAccounts", []);
        } catch (err) {
            // If we already had at least one account, treat the rejection
            // as transient. Otherwise propagate so the caller can show a
            // rejection screen.
            if (!accounts || accounts.length === 0) throw err;
        }
    }
    if (!accounts || accounts.length === 0) {
        throw new Error("Wallet returned no accounts.");
    }

    try {
        await ensureChain();
    } catch (err) {
        // Non-fatal — see comment above.
        console.warn("ensureChain failed; continuing:", err?.shortMessage || err?.message || err);
    }

    return await provider.getSigner();
}
